import { useState, useMemo } from 'react';
import {
  Box,
  Stack,
  Typography,
  Paper,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  IconButton,
  Tooltip,
  alpha,
} from '@mui/material';
import { DataGridPro, GridColDef, GridSelectionModel } from '@mui/x-data-grid-pro';
import { TenantHomeDashboardNavigationBar } from 'components/LayoutComponent/VerticalLayout/HeaderBar';
import { useDruidSyncTenantList, useTriggerDruidSync } from 'services/v1/TenantMaster/DruidSyncService';
import { DruidSyncTenant } from 'types/api/TenantMaster/DruidSyncTypes';
import { getErrorMessage } from 'utils/Error';
import ActivityIndicator from 'components/ActivityIndicatorComponent';
import SyncIcon from '@mui/icons-material/Sync';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import GroupsIcon from '@mui/icons-material/Groups';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';

// ── Shared constants & helpers ──

const STATUS_CONFIG: Record<string, { color: string; bg: string }> = {
  DEPLOYED: { color: '#42BB93', bg: '#E8F8F2' },
  CREATED: { color: '#00B3DC', bg: '#E5F7FC' },
  PENDING: { color: '#E8833A', bg: '#FFF3E8' },
  FAILED: { color: '#BB4942', bg: '#FDECEB' },
  ARCHIVED: { color: '#828C99', bg: '#F3F6F9' },
};

function getStatusConfig(status: string) {
  return STATUS_CONFIG[status] || { color: '#828C99', bg: '#F3F6F9' };
}

function StatusChip({ status }: { status: string }) {
  const config = getStatusConfig(status);
  return (
    <Chip
      label={status?.replace(/_/g, ' ') || '-'}
      size='small'
      sx={{
        bgcolor: config.bg,
        color: config.color,
        fontWeight: 600,
        fontSize: 11,
        borderRadius: 1,
      }}
    />
  );
}

function SummaryCard(props: { label: string; value: number; color: string; bg: string; icon: React.ReactNode }) {
  return (
    <Paper
      variant='outlined'
      sx={{
        flex: 1,
        p: 2,
        borderRadius: 2,
        borderColor: alpha(props.color, 0.2),
        bgcolor: props.bg,
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: `0 2px 8px ${alpha(props.color, 0.15)}` },
      }}
    >
      <Stack direction='row' alignItems='center' spacing={1.5}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1.5,
            bgcolor: alpha(props.color, 0.12),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: props.color,
          }}
        >
          {props.icon}
        </Box>
        <Box>
          <Typography variant='body2' sx={{ color: '#828C99', fontSize: 12, lineHeight: 1.2 }}>
            {props.label}
          </Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: props.color, lineHeight: 1.3 }}>
            {props.value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

const GRID_SX = {
  border: 'none',
  '& .MuiDataGrid-columnHeaders': {
    bgcolor: '#F5F7FA',
    borderBottom: '2px solid #E8ECF0',
    '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700, fontSize: 12, color: '#3B4797' },
  },
  '& .MuiDataGrid-row.odd': { bgcolor: '#FAFBFC' },
  '& .MuiDataGrid-row:hover': { bgcolor: '#F0F4FF' },
  '& .MuiDataGrid-cell': { fontSize: 12 },
};

// ── Main Page ──

export default function DruidSyncPage() {
  const { data: tenantsData, isLoading, isError, error, refetch } = useDruidSyncTenantList();
  const { mutateAsync: triggerSync, isLoading: isTriggering } = useTriggerDruidSync();

  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const tenants = useMemo(() => tenantsData?.data || [], [tenantsData]);

  const selectedTenants = useMemo(() => {
    const selectedIds = new Set(selectionModel.map(String));
    return tenants.filter((t) => selectedIds.has(t.id));
  }, [tenants, selectionModel]);

  const stats = useMemo(() => {
    return {
      total: tenants.length,
      eligible: tenants.filter((t) => t.eligibleForDruidSync).length,
      ineligible: tenants.filter((t) => !t.eligibleForDruidSync).length,
      selected: selectedTenants.length,
    };
  }, [tenants, selectedTenants]);

  const handleTriggerSync = async () => {
    const tenantIds = selectedTenants.map((t) => t.id);
    if (tenantIds.length === 0) return;
    try {
      const result = await triggerSync({ tenantIds });
      const data = result.data || {};
      const baseMessage = data.message || `Druid sync triggered for ${tenantIds.length} tenant(s)`;
      const message = data.totalTargets != null ? `${baseMessage} (${data.totalTargets} targets)` : baseMessage;
      setConfirmOpen(false);
      setSelectionModel([]);
      setSnackbar({ message, severity: 'success' });
      refetch();
    } catch (err: any) {
      setSnackbar({ message: getErrorMessage(err), severity: 'error' });
    }
  };

  const columns = useMemo<GridColDef<DruidSyncTenant>[]>(
    () => [
      { field: 'tenantCode', headerName: 'Tenant Code', width: 140 },
      {
        field: 'displayName',
        headerName: 'Tenant',
        flex: 1,
        minWidth: 220,
        renderCell: (params) => (
          <Box sx={{ overflow: 'hidden' }}>
            <Typography variant='body2' sx={{ fontSize: 12, fontWeight: 600, color: '#141414', lineHeight: 1.3 }}>
              {params.row.displayName || params.row.tenantName}
            </Typography>
            {params.row.tenantName && params.row.tenantName !== params.row.displayName && (
              <Typography variant='body2' sx={{ fontSize: 11, color: '#828C99', lineHeight: 1.2 }}>
                {params.row.tenantName}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        field: 'organizationName',
        headerName: 'Organization',
        width: 240,
        renderCell: (params) => (
          <Box sx={{ overflow: 'hidden' }}>
            <Typography variant='body2' sx={{ fontSize: 12, color: '#141414', lineHeight: 1.3 }}>
              {params.row.organizationName || '-'}
            </Typography>
            {params.row.organizationCode && (
              <Typography variant='body2' sx={{ fontSize: 11, color: '#828C99', lineHeight: 1.2 }}>
                {params.row.organizationCode}
              </Typography>
            )}
          </Box>
        ),
      },
      { field: 'status', headerName: 'Status', width: 140, renderCell: (params) => <StatusChip status={params.value} /> },
      {
        field: 'eligibleForDruidSync',
        headerName: 'Eligibility',
        width: 320,
        sortable: false,
        renderCell: (params) => {
          if (params.row.eligibleForDruidSync) {
            return (
              <Chip
                icon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
                label='Eligible'
                size='small'
                sx={{
                  bgcolor: '#E8F8F2',
                  color: '#42BB93',
                  fontWeight: 600,
                  fontSize: 11,
                  borderRadius: 1,
                  '& .MuiChip-icon': { color: '#42BB93' },
                }}
              />
            );
          }
          return (
            <Stack direction='row' spacing={1} alignItems='center' sx={{ overflow: 'hidden' }}>
              <Chip
                icon={<BlockIcon sx={{ fontSize: 16 }} />}
                label='Ineligible'
                size='small'
                sx={{
                  bgcolor: '#FDECEB',
                  color: '#BB4942',
                  fontWeight: 600,
                  fontSize: 11,
                  borderRadius: 1,
                  flexShrink: 0,
                  '& .MuiChip-icon': { color: '#BB4942' },
                }}
              />
              {params.row.ineligibleReason && (
                <Tooltip title={params.row.ineligibleReason} arrow>
                  <Typography
                    variant='body2'
                    sx={{ fontSize: 11, color: '#828C99', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {params.row.ineligibleReason}
                  </Typography>
                </Tooltip>
              )}
            </Stack>
          );
        },
      },
    ],
    []
  );

  return (
    <TenantHomeDashboardNavigationBar>
      <Stack spacing={2} p={3}>
        {/* Header */}
        <Stack direction='row' justifyContent='space-between' alignItems='flex-start'>
          <Box>
            <Typography variant='body1' fontWeight='bold'>
              Druid Sync
            </Typography>
            <Typography variant='body2' sx={{ color: '#828C99', fontSize: 12 }}>
              Select eligible tenants and trigger a Druid synchronization run
            </Typography>
          </Box>
          <Button
            variant='contained'
            size='small'
            startIcon={<SyncIcon />}
            onClick={() => setConfirmOpen(true)}
            disabled={stats.selected === 0 || isTriggering}
            sx={{
              textTransform: 'none',
              bgcolor: '#42BB93',
              '&:hover': { bgcolor: '#38A882' },
              '&.Mui-disabled': { bgcolor: '#E8ECF0' },
            }}
          >
            {`Trigger Druid Sync${stats.selected > 0 ? ` (${stats.selected})` : ''}`}
          </Button>
        </Stack>

        {/* Summary cards */}
        <Stack direction='row' spacing={2}>
          <SummaryCard label='Total Tenants' value={stats.total} color='#3B4797' bg='#F5F6FC' icon={<GroupsIcon />} />
          <SummaryCard label='Eligible' value={stats.eligible} color='#42BB93' bg='#E8F8F2' icon={<CheckCircleOutlineIcon />} />
          <SummaryCard label='Ineligible' value={stats.ineligible} color='#BB4942' bg='#FDECEB' icon={<BlockIcon />} />
          <SummaryCard label='Selected' value={stats.selected} color='#00B3DC' bg='#E5F7FC' icon={<PlaylistAddCheckIcon />} />
        </Stack>

        {isError && <Alert severity='error'>{getErrorMessage(error)}</Alert>}

        {/* Tenant table */}
        <Paper variant='outlined' sx={{ borderRadius: 2 }}>
          <Box sx={{ height: 560 }}>
            <DataGridPro
              rows={tenants}
              columns={columns}
              loading={isLoading}
              getRowId={(row) => row.id}
              checkboxSelection
              disableSelectionOnClick
              isRowSelectable={(params) => params.row.eligibleForDruidSync}
              selectionModel={selectionModel}
              onSelectionModelChange={(model) => setSelectionModel(model)}
              density='compact'
              getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
              sx={GRID_SX}
            />
          </Box>
        </Paper>
      </Stack>

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction='row' justifyContent='space-between' alignItems='center'>
            <Typography variant='body1' fontWeight='bold'>Trigger Druid Sync</Typography>
            <IconButton onClick={() => setConfirmOpen(false)} size='small' sx={{ color: '#98A2AE' }}><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography variant='body2' sx={{ mb: 2, color: '#828C99', fontSize: 13 }}>
            A Druid sync run will be triggered for the following {selectedTenants.length} tenant(s):
          </Typography>
          <Stack spacing={1} sx={{ maxHeight: 280, overflowY: 'auto' }}>
            {selectedTenants.map((tenant) => (
              <Paper
                key={tenant.id}
                variant='outlined'
                sx={{ p: 1.5, borderRadius: 2, borderColor: '#E8ECF0' }}
              >
                <Stack direction='row' alignItems='center' spacing={1.5}>
                  <Box sx={{ flex: 1, overflow: 'hidden' }}>
                    <Typography variant='body2' sx={{ fontWeight: 600, fontSize: 13, color: '#141414' }}>
                      {tenant.displayName || tenant.tenantName}
                    </Typography>
                    <Typography variant='body2' sx={{ fontSize: 11, color: '#828C99' }}>
                      {tenant.tenantCode}
                      {tenant.organizationName ? ` · ${tenant.organizationName}` : ''}
                    </Typography>
                  </Box>
                  <StatusChip status={tenant.status} />
                </Stack>
              </Paper>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} variant='outlined' size='small' sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant='contained'
            size='small'
            onClick={handleTriggerSync}
            disabled={selectedTenants.length === 0 || isTriggering}
            startIcon={isTriggering ? <ActivityIndicator size={16} /> : <SyncIcon />}
            sx={{ textTransform: 'none', bgcolor: '#42BB93', '&:hover': { bgcolor: '#38A882' }, '&.Mui-disabled': { bgcolor: '#E8ECF0' } }}
          >
            {isTriggering ? 'Triggering...' : `Trigger Sync${selectedTenants.length > 0 ? ` (${selectedTenants.length})` : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {!!snackbar && (
        <Snackbar open anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} onClose={() => setSnackbar(null)} autoHideDuration={5000}>
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} variant='filled'>{snackbar.message}</Alert>
        </Snackbar>
      )}
    </TenantHomeDashboardNavigationBar>
  );
}
